(() => {
  const instances = new Map();
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smooth = (a, b, value) => {
    const t = clamp((value - a) / Math.max(0.00001, b - a));
    return t * t * (3 - 2 * t);
  };

  const DESKTOP_DIRECTIONS = [
    [-0.98, -0.18], [-0.91, -0.42], [-0.80, -0.65], [-0.61, -0.82],
    [-0.36, -0.94], [-0.08, -1.00], [0.20, -0.98], [0.47, -0.88],
    [0.70, -0.72], [0.88, -0.48], [0.98, -0.20], [1.00, 0.08],
    [0.94, 0.35], [0.81, 0.59], [0.62, 0.79], [0.37, 0.93],
    [0.08, 1.00], [-0.22, 0.98], [-0.50, 0.87], [-0.73, 0.68],
    [-0.90, 0.43], [-0.99, 0.14]
  ];

  const MOBILE_DIRECTIONS = [
    [-0.77, -0.64], [-0.52, -0.86], [-0.25, -0.97], [0.02, -1.00],
    [0.30, -0.95], [0.57, -0.82], [0.80, -0.60], [0.92, -0.38],
    [0.98, -0.10], [0.92, 0.36], [0.73, 0.68], [0.48, 0.88],
    [0.19, 0.98], [-0.10, 1.00], [-0.40, 0.92], [-0.67, 0.74],
    [-0.88, 0.47], [-0.98, 0.14]
  ];

  const normalize = ([x, y]) => {
    const length = Math.hypot(x, y) || 1;
    return [x / length, y / length];
  };

  const buildSourcePool = (hero) => {
    const templates = [...hero.querySelectorAll('template[data-lmc-hero-source]')];
    const pool = [];
    templates.forEach((template, sourceIndex) => {
      const weight = clamp(parseInt(template.dataset.weight || '1', 10), 1, 3);
      for (let i = 0; i < weight; i += 1) pool.push({ template, sourceIndex });
    });
    return pool;
  };

  const chooseSource = (pool, index, previousSource) => {
    if (!pool.length) return null;
    let chosen = pool[(index * 5 + Math.floor(index / 3) * 2) % pool.length];
    if (pool.length > 1 && chosen.sourceIndex === previousSource) {
      chosen = pool[(index * 5 + 1) % pool.length];
    }
    return chosen;
  };

  const createCards = (state) => {
    const { hero, field } = state;
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const count = Math.max(1, parseInt(mobile ? hero.dataset.densityMobile : hero.dataset.densityDesktop, 10) || (mobile ? 16 : 20));
    const directions = (mobile ? MOBILE_DIRECTIONS : DESKTOP_DIRECTIONS).map(normalize);
    const sourcePool = buildSourcePool(hero);

    field.replaceChildren();
    state.cards = [];
    state.mobile = mobile;
    if (!sourcePool.length) return;

    let previousSource = -1;
    for (let index = 0; index < count; index += 1) {
      const source = chooseSource(sourcePool, index, previousSource);
      previousSource = source.sourceIndex;
      const node = source.template.content.firstElementChild.cloneNode(true);
      node.classList.add('lmc-hero-card');
      if (node.matches('a')) node.classList.add('is-clickable');
      node.dataset.instanceIndex = String(index);
      node.setAttribute('aria-hidden', 'true');
      node.tabIndex = -1;
      field.appendChild(node);

      const direction = directions[(index * 7 + Math.floor(index / directions.length)) % directions.length];
      const phaseJitter = ((index * 37) % 11) / (count * 18);
      const phase = (index / count + phaseJitter) % 1;
      const sizeFactor = 0.82 + (((index * 29) % 9) / 9) * 0.34;
      state.cards.push({ node, direction, phase, sizeFactor });
    }
  };

  const render = (state, now) => {
    if (!state.cards.length) return;
    const { hero, field, cards, mobile } = state;
    const width = field.clientWidth || window.innerWidth;
    const height = field.clientHeight || window.innerHeight;
    const centerX = width * 0.5;
    const centerY = height * (mobile ? 0.512 : 0.5);
    const maxSize = parseFloat(getComputedStyle(hero.closest('.lmc-home')).getPropertyValue(mobile ? '--lmc-hero-max-mobile' : '--lmc-hero-max-desktop')) || (mobile ? 210 : 360);
    const cycle = Math.max(4000, parseInt(hero.dataset.cycle || '9800', 10));
    const baseTime = state.reducedMotion ? cycle * 0.36 : now;
    const maxDistance = Math.hypot(width, height) * (mobile ? 0.72 : 0.68) + maxSize * 0.42;
    const minDistance = mobile ? 9 : 12;
    const exponential = Math.exp(2.28) - 1;

    cards.forEach((card) => {
      const phase = ((baseTime / cycle) + card.phase) % 1;
      const projection = (Math.exp(2.28 * phase) - 1) / exponential;
      const distance = minDistance + projection * maxDistance;
      const x = centerX + card.direction[0] * distance;
      const y = centerY + card.direction[1] * distance;
      const scale = (0.105 + projection * 0.895) * card.sizeFactor;
      const fadeIn = smooth(0.0, 0.11, phase);
      const fadeOut = 1 - smooth(0.94, 1.0, phase);
      const depthOpacity = 0.12 + 0.88 * smooth(0.04, 0.64, phase);
      const opacity = fadeIn * fadeOut * depthOpacity;
      const cardWidth = maxSize * card.sizeFactor;

      card.node.style.setProperty('--lmc-card-width', `${cardWidth.toFixed(2)}px`);
      card.node.style.setProperty('--lmc-card-x', `${x.toFixed(2)}px`);
      card.node.style.setProperty('--lmc-card-y', `${y.toFixed(2)}px`);
      card.node.style.setProperty('--lmc-card-scale', scale.toFixed(5));
      card.node.style.setProperty('--lmc-card-opacity', opacity.toFixed(4));
    });
  };

  const startLoop = (state) => {
    if (state.raf || state.reducedMotion) return;
    const frame = (now) => {
      state.raf = 0;
      if (state.visible && !document.hidden) {
        render(state, now);
        state.raf = requestAnimationFrame(frame);
      }
    };
    state.raf = requestAnimationFrame(frame);
  };

  const stopLoop = (state) => {
    if (!state.raf) return;
    cancelAnimationFrame(state.raf);
    state.raf = 0;
  };

  const initHero = (hero) => {
    if (instances.has(hero)) return;
    const field = hero.querySelector('[data-lmc-hero-field]');
    if (!field) return;

    const state = {
      hero,
      field,
      cards: [],
      mobile: false,
      visible: true,
      raf: 0,
      reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      resizeTimer: 0
    };

    createCards(state);
    render(state, performance.now());

    state.observer = new IntersectionObserver((entries) => {
      state.visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0);
      if (state.visible) startLoop(state);
      else stopLoop(state);
    }, { threshold: [0, 0.01] });
    state.observer.observe(hero);

    state.onResize = () => {
      clearTimeout(state.resizeTimer);
      state.resizeTimer = window.setTimeout(() => {
        const nextMobile = window.matchMedia('(max-width: 749px)').matches;
        if (nextMobile !== state.mobile) createCards(state);
        render(state, performance.now());
      }, 120);
    };
    window.addEventListener('resize', state.onResize, { passive: true });
    instances.set(hero, state);
    startLoop(state);
  };

  const destroyHero = (hero) => {
    const state = instances.get(hero);
    if (!state) return;
    stopLoop(state);
    state.observer?.disconnect();
    window.removeEventListener('resize', state.onResize);
    instances.delete(hero);
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-lmc-hero]').forEach(initHero);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-lmc-hero]').forEach(destroyHero);
  });
  document.addEventListener('visibilitychange', () => {
    instances.forEach((state) => {
      if (document.hidden) stopLoop(state);
      else if (state.visible) startLoop(state);
    });
  });
})();
